import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllDocuments, deleteDocument } from '../services/api.service';
import Loading from '../components/loading';
import { FaTimes } from 'react-icons/fa';
import Helper from '../services/helper.service';
import { useConfirm } from '../components/confirm-dialog';

const DocumentsListPage = () => {
    const [documents, setDocuments] = useState([]);
    const [filteredDocuments, setFilteredDocuments] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const confirm = useConfirm();

    useEffect(() => {
        const fetchDocuments = async () => {
            setLoading(true);

            try {
                const data = await getAllDocuments();
                setDocuments(data);
                setFilteredDocuments(data);

            } catch {
                Helper.toast("Erreur lors du chargement des documents", "error");

            } finally {
                setLoading(false);
            }
        };

        fetchDocuments();
    }, []);

    useEffect(() => {
        setFilteredDocuments(
            documents.filter((doc) =>
                doc.name?.fr?.toLowerCase().includes(search.toLowerCase())
            )
        );
    }, [search, documents]);

    const handleDelete = async (document) => {
        if (!document._id) return;

        const ok = await confirm({
            title: "Attention !",
            message: <>Supprimer ce document <strong>{document.name.fr}</strong> ?</>,
            confirmText: "Oui",
            cancelText: "Non",
            tone: "warning",
        });

        if (!ok) return Helper.toast("Action annulée", "info");

        try {
            await deleteDocument(document._id);
            setDocuments((prev) => prev.filter((d) => d._id !== document._id));

        } catch {
            Helper.toast("Erreur lors de la suppression", "error");
        }
    };

    return (
        <section className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Gestion des documents</h1>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div className="relative w-full sm:max-w-sm">
                    <input
                        type="text"
                        placeholder="Rechercher un document"
                        className="w-full border border-gray-300 p-2 pr-10 rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button
                            onClick={() => setSearch('')}
                            className="absolute right-3 top-3 text-gray-500 text-lg"
                        >
                            <FaTimes className="w-4 h-4" />
                        </button>
                    )}
                </div>

                <button
                    className="bg-primary hover:bg-blue-700 text-white px-4 py-2 rounded shadow"
                    onClick={() => navigate('/document/new')}
                >
                    Ajouter un document
                </button>
            </div>

            {/* Document List */}
            {loading ? (
                <div className="flex justify-center py-24">
                    <Loading />
                </div>
            ) : filteredDocuments.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {filteredDocuments.map((doc) => (
                        <div
                            key={doc._id}
                            className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition flex flex-col justify-between"
                        >
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                {doc.name?.fr || 'Sans titre'}
                            </h3>

                            {doc.link?.fr && (
                                <a
                                    href={doc.link.fr}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-primary underline mb-3 inline-block"
                                >
                                    Voir le document
                                </a>
                            )}

                            <div className="flex gap-2 mt-4">
                                <button
                                    onClick={() => navigate(`/document/${doc._id}`)}
                                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm py-2 px-3 rounded shadow-sm"
                                >
                                    Modifier
                                </button>
                                <button
                                    onClick={() => handleDelete(doc)}
                                    className="flex-1 bg-red-100 hover:bg-red-200 text-red-700 text-sm py-2 px-3 rounded shadow-sm"
                                >
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-center text-gray-500 mt-10">
                    Aucun document trouvé.
                </p>
            )}
        </section>
    );
};

export default DocumentsListPage;